/**
 * @module ReasoningStaleBanner
 * @purpose Amber banner shown above the evidence cluster list when the run's evidence base has
 *   changed since it ran — saying WHICH of the two causes applies, and how much evidence the run
 *   set aside.
 * @depends react-i18next, types/reasoning
 * @dependedOnBy CanvasReasoningPanel
 *
 * @rules
 * - Performance: pure display — no data fetching
 * - State: none
 * - Errors: n/a
 * - Contract: a null count means NOT MEASURED, not zero. Rendering null as "0" would tell the
 *   investigator that nothing was excluded from a run nobody ever checked. The two must read
 *   differently, and the counts are approximate (no shared read snapshot — FR8), so the wording
 *   never claims an exact figure.
 * - Note: no --warning token exists; amber styling uses amber-600 / amber-50 per the
 *   brand-identity semantic-colours table (state = Warning). CSS variables preferred; amber
 *   Tailwind classes are explicitly permitted for semantic states in the brand skill.
 */

import { useTranslation } from 'react-i18next'

import type { StaleReason } from '../../../../types/reasoning'

export interface ReasoningStaleInfo {
  /** Absent on a legacy run or the POST create response — falls back to neutral wording. */
  staleReason?: StaleReason | null
  /** null = not measured. NOT zero. */
  excludedEvidenceFileCount?: number | null
  /** null = not measured. NOT zero. */
  excludedArtefactRowCount?: number | null
}

interface ReasoningStaleBannerProps {
  stale: ReasoningStaleInfo
  onRerun: () => void
}

const BANNER_STYLE: React.CSSProperties = {
  background: 'rgba(217, 119, 6, 0.08)',
  borderBottom: '1px solid rgba(217, 119, 6, 0.25)',
  color: 'rgb(146, 64, 14)',
}

const DARK_BANNER_OVERRIDE = 'dark:text-amber-300 dark:border-amber-700/40 dark:bg-amber-900/10'

// `as const` keeps the literal key types i18next's generated key union requires;
// `satisfies` keeps the exhaustiveness check, so a new StaleReason variant fails
// to compile here rather than silently rendering the neutral fallback.
const REASON_KEY = {
  frontier_changed: 'reasoning.stale.frontierChanged',
  evidence_superseded: 'reasoning.stale.evidenceSuperseded',
  both: 'reasoning.stale.both',
} as const satisfies Record<StaleReason, string>

/**
 * Amber stale-evidence banner with a re-run reasoning action.
 *
 * @behaviour Renders inline above the evidence cluster list when the run is stale. The headline
 *            varies by staleReason; an absent reason falls back to neutral wording rather than
 *            asserting a cause it does not know. The excluded-evidence line renders only when
 *            evidence supersede is one of the causes, since it is meaningless otherwise, and
 *            distinguishes "not recorded" from a measured zero.
 * @errors n/a — purely presentational
 */
export function ReasoningStaleBanner({ stale, onRerun }: ReasoningStaleBannerProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  // Normalise absent-vs-null once, so the rest reads in one currency. Both mean
  // "not measured"; neither means zero.
  const reason = stale.staleReason ?? null
  const fileCount = stale.excludedEvidenceFileCount ?? null
  const rowCount = stale.excludedArtefactRowCount ?? null

  const headlineKey = reason === null ? 'reasoning.stale.unknown' : REASON_KEY[reason]
  const supersedeApplies = reason === 'evidence_superseded' || reason === 'both'
  // Both halves must be present: reporting one and inventing the other would be worse
  // than reporting neither.
  const measured = fileCount !== null && rowCount !== null

  return (
    <div
      role="status"
      className={`flex items-start justify-between px-3 py-2 text-xs ${DARK_BANNER_OVERRIDE}`}
      style={BANNER_STYLE}
    >
      <span style={{ fontFamily: 'var(--font-body)' }}>
        {t(headlineKey)}
        {supersedeApplies && (
          <>
            {' '}
            {measured
              ? t('reasoning.stale.excluded', { count: fileCount, rows: rowCount })
              : t('reasoning.stale.excludedNotRecorded')}
          </>
        )}
      </span>
      <button
        type="button"
        onClick={onRerun}
        className="ml-3 shrink-0 uppercase tracking-wide underline hover:no-underline"
        style={{ fontFamily: 'var(--font-mono)', color: 'inherit' }}
      >
        {t('reasoning.stale.rerun')}
      </button>
    </div>
  )
}
